import { MaximizeViewerRender } from "./maximize-viewer-render";
import { maximizeViewerForm } from "./maximize-viewer-form";
import { MaximizeViewerListener } from "./maximize-viewer-listener";
import { buildMaximizeEmbedUrl } from "./maximize-viewer-embed-utils";
import { directMaximizeReadTracker } from "../read/direct-maximize-read-tracker";

export class MaximizeViewerSetup {
    private static initialized = false;
    private static modal: HTMLElement | null = null;
    private static iframe: HTMLIFrameElement | null = null;
    private static img: HTMLImageElement | null = null;
    private static video: HTMLVideoElement | null = null;

    public static init(): void {
        if (this.initialized) return;
        this.initialized = true;

        const modal = MaximizeViewerRender.buildModal();
        document.body.appendChild(modal);
        this.modal = modal;
        this.iframe = maximizeViewerForm.getIframe(modal);
        this.img = maximizeViewerForm.getImg(modal);
        this.video = maximizeViewerForm.getVideo(modal);

        MaximizeViewerListener.bindClose(maximizeViewerForm.getCloseBtn(modal), () => this.close());
        MaximizeViewerListener.bindBackdrop(maximizeViewerForm.getBackdrop(modal), () => this.close());
        MaximizeViewerListener.bindModalBody(maximizeViewerForm.getModalBody(modal), () => this.close());
        MaximizeViewerListener.bindEscapeKey(modal, () => this.close());

        // Capture phase — fires before bubble-phase handlers (e.g. board card's detail modal opener).
        MaximizeViewerListener.bindDocumentClickCapture((event) => {
            const target = event.target;
            if (!(target instanceof Element)) return;

            // 1. Overlay on SNS embeds in detail/modal view
            const overlay = maximizeViewerForm.getMaximizeOverlay(target);
            if (overlay instanceof HTMLElement) {
                const container = maximizeViewerForm.getSnsContainer(overlay);
                if (container instanceof HTMLElement) {
                    event.preventDefault();
                    event.stopPropagation();
                    this.open(
                        container.getAttribute("data-sns-url") ?? "",
                        container.getAttribute("data-sns-platform") ?? "",
                    );
                    return;
                }
            }

            // 2. Product detail image (works anywhere)
            const productImg = maximizeViewerForm.getProductImageMaximize(target);
            if (productImg instanceof HTMLImageElement && productImg.src) {
                event.preventDefault();
                event.stopPropagation();
                this.openImage(productImg.src);
                return;
            }

            // Cases 3-5 fire inside the board modal body, the modeless show page, or a
            // DIRECT_MAXIMIZE card. List cards (board-card-list__media) are deliberately
            // excluded — the tile shows only one image, and clicking it should behave like
            // the rest of the card (open the detail view) rather than a redundant lightbox.
            const inModal = maximizeViewerForm.getElementModalBody(target);
            const inModeless = maximizeViewerForm.getPostDetailModeless(target);
            const inDirectMaximize = maximizeViewerForm.getDirectMaximizeCard(target);
            if (!inModal && !inModeless && !inDirectMaximize) return;

            // 3. Any SNS tile inside board modal
            // Skip if the click is on the tile remove (×) button — let the bubble-phase handler remove the URL.
            if (!maximizeViewerForm.getSnsTileRemove(target)) {
                const tile = maximizeViewerForm.getSnsTile(target);
                if (tile instanceof HTMLElement) {
                    const snsUrl = tile.getAttribute("data-sns-url") ?? "";
                    const snsPlatform = tile.getAttribute("data-sns-platform") ?? "";
                    if (snsUrl && snsPlatform) {
                        event.preventDefault();
                        event.stopPropagation();
                        this.open(snsUrl, snsPlatform);
                        directMaximizeReadTracker.trackFromCard(inDirectMaximize);
                        return;
                    }
                    // Image tile in sns-preview-tiles grid (no sns-url/platform): open image in maximize viewer.
                    const imgEl = maximizeViewerForm.getImage(tile);
                    if (imgEl instanceof HTMLImageElement && imgEl.src) {
                        event.preventDefault();
                        event.stopPropagation();
                        this.openImage(imgEl.src);
                        directMaximizeReadTracker.trackFromCard(inDirectMaximize);
                        return;
                    }
                }
            }

            // 4. Attached post images inside board modal
            // Case A: .board-image-item wrapper (post-detail-media-preview style)
            // Case B: direct <img> in board-images-grid (board-media-image style)
            const imageItem = maximizeViewerForm.getImageItem(target);
            const imgEl = imageItem
                ? maximizeViewerForm.getImage(imageItem)
                : maximizeViewerForm.getBoardGridImage(target);
            if (imgEl instanceof HTMLImageElement && imgEl.src) {
                event.preventDefault();
                event.stopPropagation();
                this.openImage(imgEl.src);
                directMaximizeReadTracker.trackFromCard(inDirectMaximize);
                return;
            }

            // 4b. OGP preview cards inside board modal
            const ogpCard = maximizeViewerForm.getOgpPreviewCard(target);
            if (ogpCard instanceof HTMLElement) {
                const snsUrl = ogpCard.getAttribute("data-sns-url") ?? "";
                const platform = ogpCard.getAttribute("data-platform") ?? "";
                if (snsUrl && platform) {
                    event.preventDefault();
                    event.stopPropagation();
                    this.open(snsUrl, platform);
                    directMaximizeReadTracker.trackFromCard(inDirectMaximize);
                    return;
                }
                const imgEl = maximizeViewerForm.getOgpPreviewCardImage(ogpCard);
                if (imgEl instanceof HTMLImageElement && imgEl.src) {
                    event.preventDefault();
                    event.stopPropagation();
                    this.openImage(imgEl.src);
                    directMaximizeReadTracker.trackFromCard(inDirectMaximize);
                    return;
                }
            }

            // 5. Attached post videos inside board modal
            const videoWrap = maximizeViewerForm.getVideoWrap(target);
            if (videoWrap instanceof HTMLElement) {
                // Prefer <source> (no thumbnail case); fall back to img[data-video-url] (thumbnail case)
                const source = maximizeViewerForm.getVideoSource(videoWrap);
                const src = source?.getAttribute("src")
                    ?? maximizeViewerForm.getVideoThumb(videoWrap)?.getAttribute("data-video-url")
                    ?? "";
                if (src) {
                    event.preventDefault();
                    event.stopPropagation();
                    this.openVideo(src);
                    directMaximizeReadTracker.trackFromCard(inDirectMaximize);
                }
            }
        });
    }

    private static show(): void {
        if (!this.modal) return;
        this.modal.hidden = false;
        document.documentElement.classList.add("maximize-viewer-open");
    }

    public static open(url: string, platform: string): void {
        const embedUrl = buildMaximizeEmbedUrl(url, platform);
        if (!embedUrl || !this.iframe || !this.img || !this.video) return;

        this.img.hidden = true;
        this.img.src = "";
        this.video.pause();
        this.video.src = "";
        this.video.hidden = true;
        this.iframe.dataset.platform = platform;
        this.iframe.hidden = false;
        this.iframe.src = embedUrl;

        this.show();
    }

    public static openImage(src: string): void {
        if (!src || !this.iframe || !this.img || !this.video) return;

        this.iframe.hidden = true;
        this.iframe.src = "";
        this.video.hidden = true;
        this.video.pause();
        this.video.src = "";
        this.img.src = src;
        this.img.hidden = false;
        this.show();
    }

    public static openVideo(src: string): void {
        const cleanSrc = src.replace(/#.*$/, "");
        if (!cleanSrc || !this.iframe || !this.img || !this.video) return;

        this.iframe.hidden = true;
        this.iframe.src = "";
        this.img.hidden = true;
        this.img.src = "";
        this.video.src = cleanSrc;
        this.video.load();
        this.video.hidden = false;
        this.show();
        void this.video.play().catch(() => undefined);
    }

    public static close(): void {
        if (!this.modal || !this.iframe || !this.img || !this.video) return;

        this.modal.hidden = true;
        this.iframe.src = "";
        delete this.iframe.dataset.platform;
        this.iframe.hidden = true;
        this.img.src = "";
        this.img.hidden = true;
        this.video.pause();
        this.video.src = "";
        this.video.hidden = true;
        document.documentElement.classList.remove("maximize-viewer-open");
    }
}
